import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import AppStoreBadge from '@deliveroo/consumer-component-library/components/AppStoreBadge';
import Spacer from '@deliveroo/consumer-component-library/components/Spacer';
import NavFooter from '@deliveroo/consumer-component-library/components/NavFooter';
import classnames from 'classnames';
import queryString from 'query-string';
import { isFeatureActive } from '../../selectors/configSelectors';
import {
  ESCDA_AWARD_LOGO,
  GR_CLP_CUISINES_FOOTER_LINK,
  GR_CLP_BRANDS_FOOTER_LINK,
} from '../../flags/features';
import { urlWithLocale } from '../../utils/locale';
import i18n from '../../utils/i18n';
import styles from './styles.scss';

const PageFooter = ({
  social,
  newsroomLink,
  careersLink,
  hasBlog,
  currentCountry,
  locale,
  showESCDAAwardLogo,
  showCuisinesLink,
  showBrandsLink,
  isHiddenOnMobile,
  riderInviteLink,
}) => {
  const addLocaleToUrl = (url) => urlWithLocale(currentCountry, url, locale);
  const currentYear = new Date().getFullYear();
  const wrapperClassNames = classnames({ [styles.isHiddenOnMobile]: isHiddenOnMobile });

  const rideWithUsParams = {
    'utm-campaign': 'ridewithus',
    'utm-medium': 'organic',
    'utm-source': 'sitefooter',
  };

  const footerSections = [
    {
      heading: i18n.gettext('Discover Deliveroo'),
      links: [
        { text: i18n.gettext('footer.about_us'), url: addLocaleToUrl('/about-us') },
        ...(newsroomLink ? [{ text: i18n.gettext('footer.newsroom'), url: newsroomLink }] : []),
        ...(hasBlog
          ? [
              {
                text: i18n.gettext('footer.discover.foodscene_blog'),
                url: `https://foodscene.${currentCountry.host}`,
              },
            ]
          : []),
        {
          text: i18n.gettext('footer.discover.engineering_blog'),
          url: 'https://deliveroo.engineering',
        },
        { text: i18n.gettext('footer.discover.design_blog'), url: 'https://deliveroo.design' },
        { text: i18n.gettext('footer.discover.careers'), url: careersLink },
        {
          text: i18n.gettext('footer.restaurant_sign_up'),
          url: currentCountry.restaurantsHomepageUrl,
        },
        ...(riderInviteLink
          ? [
              {
                text: i18n.gettext('footer.discover.invite_rider_cta'),
                url: addLocaleToUrl(`/apply?${queryString.stringify(rideWithUsParams)}`),
              },
            ]
          : []),
      ],
    },
    {
      heading: i18n.gettext('Legal'),
      links: [
        { text: i18n.gettext('footer.legal.terms_and_conditions'), url: addLocaleToUrl('/legal') },
        { text: i18n.gettext('footer.legal.privacy'), url: addLocaleToUrl('/privacy') },
        { text: i18n.gettext('footer.legal.cookies'), url: addLocaleToUrl('/cookies') },
        ...(currentCountry.tld === 'uk'
          ? [
              {
                text: i18n.gettext('footer.legal.modern_slavery_statement'),
                url: addLocaleToUrl('/modern-slavery-act-statement'),
              },
            ]
          : []),
      ],
    },
    {
      heading: i18n.gettext('Help'),
      links: [
        { text: i18n.gettext('footer.help.contact'), url: addLocaleToUrl('/contact') },
        { text: i18n.gettext('footer.help.faq'), url: addLocaleToUrl('/faq') },
        ...(currentCountry.tld === 'de'
          ? [{ text: i18n.gettext('footer.help.impressum'), url: addLocaleToUrl('/imprint') }]
          : []),
        ...(showCuisinesLink
          ? [{ text: i18n.gettext('footer.help.cuisines'), url: addLocaleToUrl('/cuisines') }]
          : []),
        ...(showBrandsLink
          ? [{ text: i18n.gettext('footer.help.brands'), url: addLocaleToUrl('/brands') }]
          : []),
        { text: i18n.gettext('footer.help.site_map'), url: addLocaleToUrl('/sitemap') },
      ],
      children: showESCDAAwardLogo && <div className={styles.escda} />,
    },
    {
      heading: i18n.gettext('Take Deliveroo with you'),
      children: (
        <div>
          <a href="/app?platform=ios" target="_blank" rel="noopener noreferrer">
            <AppStoreBadge type="ios" text={i18n.gettext('Download Deliveroo in the App Store')} />
          </a>
          <Spacer spacing="medium 0">
            <a href="/app?platform=android" target="_blank" rel="noopener noreferrer">
              <AppStoreBadge
                type="android"
                text={i18n.gettext('Download Deliveroo in the App Store')}
              />
            </a>
          </Spacer>
        </div>
      ),
    },
  ];

  const socialLinks = [
    { title: 'Facebook', url: `https://www.facebook.com/${social.facebook}`, icon: 'facebook' },
    { title: 'Twitter', url: `https://twitter.com/${social.twitter}`, icon: 'twitter' },
    { title: 'Instagram', url: `https://instagram.com/${social.instagram}`, icon: 'instagram' },
  ];

  return (
    <div className={wrapperClassNames}>
      <NavFooter
        footerSections={footerSections}
        socialLinks={socialLinks}
        copyright={`© ${currentYear} Deliveroo`}
      />
    </div>
  );
};

PageFooter.propTypes = {
  social: PropTypes.shape({
    facebook: PropTypes.string.isRequired,
    twitter: PropTypes.string.isRequired,
    instagram: PropTypes.string.isRequired,
  }).isRequired,
  newsroomLink: PropTypes.string.isRequired,
  careersLink: PropTypes.string.isRequired,
  hasBlog: PropTypes.bool.isRequired,
  currentCountry: PropTypes.shape({
    host: PropTypes.string.isRequired,
    tld: PropTypes.string.isRequired,
    restaurantsHomepageUrl: PropTypes.string.isRequired,
  }).isRequired,
  locale: PropTypes.string.isRequired,
  showESCDAAwardLogo: PropTypes.bool.isRequired,
  showCuisinesLink: PropTypes.bool.isRequired,
  showBrandsLink: PropTypes.bool.isRequired,
  isHiddenOnMobile: PropTypes.bool,
  riderInviteLink: PropTypes.bool,
};

PageFooter.defaultProps = {
  isHiddenOnMobile: false,
  riderInviteLink: false,
};

const mapStateToProps = (state) => {
  const { config, request } = state;
  const { social, newsroomLink, careersLink, blog: hasBlog } = config.countries.find(
    (country) => country.id === request.country.tld,
  );

  return {
    social,
    newsroomLink,
    careersLink,
    hasBlog,
    currentCountry: request.country,
    locale: request.locale,
    /** The French CS team won an award so we want to show the award logo on the
     * french site when the feature is on */
    showESCDAAwardLogo:
      (request.country.tld === 'fr' && isFeatureActive(state, ESCDA_AWARD_LOGO)) || false,
    showCuisinesLink: isFeatureActive(state, GR_CLP_CUISINES_FOOTER_LINK),
    showBrandsLink: isFeatureActive(state, GR_CLP_BRANDS_FOOTER_LINK),
    riderInviteLink: request.country.riderInviteLink,
  };
};

export default connect(mapStateToProps)(PageFooter);
